import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { format, startOfMonth, addMonths, isAfter } from "date-fns";
import { de } from "date-fns/locale";
import { round2, secondsToHours, calculateMonthlySoll, deriveContractParams } from "@/utils/workTimeUtils";
import type { SwissHoliday } from "@/utils/swissHolidays";
import { computeVacationWithCarryover } from "@/utils/vacationYear";
import { getEmployeeOverride } from "@/config/employeeOverrides";

// ─── Types ──────────────────────────────────────────────────────────

export interface MonthlyBreakdown {
  monthKey: string;       // "YYYY-MM"
  monthLabel: string;     // "Jul 2025"
  actualHours: number;
  expectedHours: number;  // Soll (dynamically computed)
  difference: number;
  isUnpaidLeave: boolean;
  /** Public holidays on weekdays in this month */
  holidays: { date: string; name: string }[];
  /** Company closure days (not already holidays) */
  closureDays: number;
  /** Working days used for Soll calculation */
  workingDays: number;
  /** Vacation hours in this month */
  vacationHours: number;
}

export interface VacationEntry {
  id: string;
  date: string;
  hours: number;
  description: string | null;
}

export interface EmployeeHoursSummary {
  // ── Work hours (Arbeitsstunden) ──
  totalActualHours: number;
  totalExpectedHours: number;
  hoursDifference: number;
  /** Cumulative +/- balance from ALL previous years */
  hoursCarryover: number;
  /** hoursDifference + hoursCarryover */
  totalBalance: number;

  // ── Vacation (Ferienkontingent) — clearly separate ──
  vacationEntitlement: number;   // this year's grant (prorated for partial year)
  vacationCarryover: number;     // unused hours from previous year
  vacationTaken: number;         // hours consumed this year
  vacationBalance: number;       // entitlement + carryover - taken
  vacationAccrued: number;       // prorated to current month (HR view)

  // ── Detail ──
  monthlyBreakdown: MonthlyBreakdown[];
  vacationEntries: VacationEntry[];

  // ── Meta ──
  /** Base monthly target hours (before working-day proration) */
  monthlyTarget: number;
  selectedYear: number;
  contractStartDate: Date | null;
  isLoading: boolean;
  hasEmployeeRecord: boolean;
  employeeId: string | null;
  bookVacation: (date: Date, hours: number, description?: string) => Promise<void>;
  deleteVacation: (id: string) => Promise<void>;
}

// ─── Helpers ────────────────────────────────────────────────────────

function generateMonthRange(start: Date, end: Date): string[] {
  const months: string[] = [];
  let current = startOfMonth(start);
  const endMonth = startOfMonth(end);
  while (!isAfter(current, endMonth)) {
    months.push(format(current, "yyyy-MM"));
    current = addMonths(current, 1);
  }
  return months;
}

async function ensureVacationRecord(employeeId: string, year: number): Promise<string> {
  const { data: existing } = await supabase
    .from("vacation_records")
    .select("id")
    .eq("employee_id", employeeId)
    .eq("record_year", year)
    .single();

  if (existing) return existing.id;

  const { data: created, error } = await supabase
    .from("vacation_records")
    .insert({ employee_id: employeeId, record_year: year })
    .select("id")
    .single();

  if (error) throw error;
  return created!.id;
}

// ─── Hook ───────────────────────────────────────────────────────────

export function useEmployeeHoursSummary(selectedYearProp?: number): EmployeeHoursSummary {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const now = new Date();
  const selectedYear = selectedYearProp ?? now.getFullYear();

  // ── Fetch employee contract from DB (SSOT) ──
  const { data: employee, isLoading: isLoadingEmployee } = useQuery({
    queryKey: ["employee-contract", user?.email],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("*")
        .eq("email", user!.email!.toLowerCase())
        .maybeSingle();
      if (error) throw new Error(`Employee lookup failed: ${error.message}`);
      return data;
    },
    enabled: !!user?.email,
  });

  const hasEmployeeRecord = !!employee;
  const employeeId = employee?.id ?? null;

  // Non-DB overrides (unpaid leave, contract corrections)
  const overrides = user?.email ? getEmployeeOverride(user.email) : {};
  const unpaidLeaveMonths = overrides.unpaidLeaveMonths ?? [];

  // Derive contract parameters from DB + overrides (SSOT via deriveContractParams)
  const derived = employee ? deriveContractParams(employee, overrides) : null;
  const contractStart = derived?.contractStart ?? null;
  const canton = derived?.canton ?? "ZH";
  const monthlyTarget = derived?.monthlyTarget ?? 0;
  const annualVacationHours = overrides.vacationHoursPerYear
    ?? employee?.vacation_days_per_year ?? 0;

  // ── Fetch ALL time entries from contract start (needed for carryover) ──
  const { data: allTimeEntries, isLoading: isLoadingEntries } = useQuery({
    queryKey: ["employee-hours-entries", user?.id, contractStart?.toISOString()],
    queryFn: async () => {
      if (!user || !contractStart) return [];
      const { data, error } = await supabase
        .from("time_entries")
        .select("start_time, duration")
        .eq("user_id", user.id)
        .is("deleted_at", null)
        .gte("start_time", contractStart.toISOString())
        .order("start_time", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: hasEmployeeRecord && !!contractStart,
  });

  // ── Fetch ALL vacation transactions from contract start (needed for carryover) ──
  const { data: allVacationTx, isLoading: isLoadingVacation } = useQuery({
    queryKey: ["employee-vacation-transactions", employeeId, contractStart?.toISOString()],
    queryFn: async () => {
      if (!employeeId || !contractStart) return [];
      const { data, error } = await supabase
        .from("vacation_transactions")
        .select("id, days_amount, transaction_type, transaction_date, description")
        .eq("employee_id", employeeId)
        .gte("transaction_date", contractStart.toISOString())
        .order("transaction_date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: hasEmployeeRecord && !!employeeId && !!contractStart,
  });

  // ── Mutations ──

  const { mutateAsync: bookVacationMutation } = useMutation({
    mutationFn: async ({ date, hours, description }: { date: Date; hours: number; description?: string }) => {
      if (!employeeId) throw new Error("Employee ID not resolved — cannot book vacation");
      const year = date.getFullYear();
      const recordId = await ensureVacationRecord(employeeId, year);
      const { error } = await supabase.from("vacation_transactions").insert({
        employee_id: employeeId,
        vacation_record_id: recordId,
        transaction_type: "taken",
        days_amount: hours, // days_amount stores HOURS for hours-based contracts
        transaction_date: format(date, "yyyy-MM-dd"),
        description: description || `Ferien: ${format(date, "dd.MM.yyyy", { locale: de })}`,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee-vacation-transactions"] });
    },
  });

  const { mutateAsync: deleteVacationMutation } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("vacation_transactions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee-vacation-transactions"] });
    },
  });

  const bookVacation = async (date: Date, hours: number, description?: string) => {
    await bookVacationMutation({ date, hours, description });
  };

  const deleteVacation = async (id: string) => {
    await deleteVacationMutation(id);
  };

  // ── Loading / empty state ──

  const isLoading = isLoadingEmployee || isLoadingEntries || isLoadingVacation;

  const emptyResult: EmployeeHoursSummary = {
    totalActualHours: 0,
    totalExpectedHours: 0,
    hoursDifference: 0,
    hoursCarryover: 0,
    totalBalance: 0,
    vacationEntitlement: 0,
    vacationCarryover: 0,
    vacationTaken: 0,
    vacationBalance: 0,
    vacationAccrued: 0,
    monthlyBreakdown: [],
    vacationEntries: [],
    monthlyTarget,
    selectedYear,
    contractStartDate: contractStart,
    isLoading,
    hasEmployeeRecord,
    employeeId,
    bookVacation,
    deleteVacation,
  };

  if (!hasEmployeeRecord || isLoading || !allTimeEntries || !contractStart) return emptyResult;

  // ════════════════════════════════════════════════════════════════════
  //  COMPUTATION — all pure, no React state
  // ════════════════════════════════════════════════════════════════════

  const todayStr = format(now, "yyyy-MM-dd");
  const currentMonthKey = format(now, "yyyy-MM");

  // ── Group time entries by month ──
  const hoursByMonth: Record<string, number> = {};
  for (const entry of allTimeEntries) {
    if (entry.duration == null) continue;
    const mk = format(new Date(entry.start_time), "yyyy-MM");
    hoursByMonth[mk] = (hoursByMonth[mk] || 0) + secondsToHours(entry.duration);
  }

  // ── Vacation hours per month (for Soll reduction).
  //    Current month: only count dates ≤ today. ──
  const vacationByMonth: Record<string, number> = {};
  for (const t of (allVacationTx || [])) {
    if (t.transaction_type !== "taken") continue;
    const mk = t.transaction_date.substring(0, 7);
    if (mk === currentMonthKey && t.transaction_date > todayStr) continue;
    vacationByMonth[mk] = (vacationByMonth[mk] || 0) + (t.days_amount || 0);
  }

  // ── Determine month range for the selected year ──
  const yearStart = new Date(selectedYear, 0, 1);
  const yearEnd = new Date(selectedYear, 11, 31);
  const rangeStart = contractStart > yearStart ? contractStart : yearStart;
  const rangeEnd = now < yearEnd ? now : yearEnd;

  const selectedYearMonths = generateMonthRange(rangeStart, rangeEnd);

  // ── Monthly breakdown for selected year ──
  const monthlyBreakdown: MonthlyBreakdown[] = selectedYearMonths.map((monthKey) => {
    const [yr, mo] = monthKey.split("-").map(Number);
    const isUnpaidLeave = unpaidLeaveMonths.includes(monthKey);
    const actualHours = round2(hoursByMonth[monthKey] || 0);
    const vacationInMonth = vacationByMonth[monthKey] || 0;
    const isCurrentMonth = currentMonthKey === monthKey;
    const isBeforeNow = monthKey < currentMonthKey;

    // Dynamic Soll via swissHolidays + workTimeUtils
    const sollResult = calculateMonthlySoll(monthlyTarget, yr, mo, canton, {
      includeClosures: true,
      contractStart: contractStart > new Date(yr, mo - 1, 1) ? contractStart : undefined,
      currentDate: isCurrentMonth ? now : undefined,
      vacationHours: vacationInMonth,
      isUnpaidLeave,
    });

    // Future months (not current, not past) have Soll = 0
    const expectedHours = (isCurrentMonth || isBeforeNow) ? sollResult.soll : 0;
    const difference = round2(actualHours - expectedHours);

    return {
      monthKey,
      monthLabel: format(new Date(yr, mo - 1), "MMM yyyy", { locale: de }),
      actualHours,
      expectedHours,
      difference,
      isUnpaidLeave,
      holidays: sollResult.holidays.map((h) => ({
        date: format(h.date, "dd.MM.yyyy"),
        name: h.name,
      })),
      closureDays: sollResult.closureDays,
      workingDays: sollResult.workingDays,
      vacationHours: vacationInMonth,
    };
  });

  // ── Totals for selected year ──
  const totalActualHours = round2(
    monthlyBreakdown.reduce((s, m) => s + m.actualHours, 0)
  );
  const totalExpectedHours = round2(
    monthlyBreakdown.reduce((s, m) => s + m.expectedHours, 0)
  );
  const hoursDifference = round2(totalActualHours - totalExpectedHours);

  // ── Hours carryover from all previous years ──
  let hoursCarryover = 0;
  if (contractStart.getFullYear() < selectedYear) {
    // Compute cumulative Ist - Soll for every month before selectedYear
    const carryoverEndMonth = `${selectedYear}-01`;
    const allPrevMonths = generateMonthRange(contractStart, new Date(selectedYear - 1, 11, 31));
    for (const mk of allPrevMonths) {
      if (mk >= carryoverEndMonth) break;
      const [yr, mo] = mk.split("-").map(Number);
      const isUL = unpaidLeaveMonths.includes(mk);
      const actual = hoursByMonth[mk] || 0;
      const vacInMo = vacationByMonth[mk] || 0;
      const prevSoll = calculateMonthlySoll(monthlyTarget, yr, mo, canton, {
        includeClosures: true,
        contractStart: contractStart > new Date(yr, mo - 1, 1) ? contractStart : undefined,
        vacationHours: vacInMo,
        isUnpaidLeave: isUL,
      });
      hoursCarryover += actual - prevSoll.soll;
    }
    hoursCarryover = round2(hoursCarryover);
  }

  const totalBalance = round2(hoursDifference + hoursCarryover);

  // ── Vacation computation via vacationYear module ──
  const vacSummary = computeVacationWithCarryover(
    selectedYear,
    contractStart,
    annualVacationHours,
    (allVacationTx || []).map((t) => ({
      transaction_type: t.transaction_type,
      transaction_date: t.transaction_date,
      days_amount: t.days_amount,
    })),
    now,
    unpaidLeaveMonths
  );

  // Vacation entries for the selected year
  const yearStartStr = `${selectedYear}-01-01`;
  const yearEndStr = `${selectedYear + 1}-01-01`;
  const takenTx = (allVacationTx || []).filter(
    (t) =>
      t.transaction_type === "taken" &&
      t.transaction_date >= yearStartStr &&
      t.transaction_date < yearEndStr
  );
  const vacationEntries: VacationEntry[] = takenTx.map((t) => ({
    id: t.id,
    date: t.transaction_date,
    hours: t.days_amount,
    description: t.description,
  }));

  return {
    totalActualHours,
    totalExpectedHours,
    hoursDifference,
    hoursCarryover,
    totalBalance,
    vacationEntitlement: vacSummary.entitlement,
    vacationCarryover: vacSummary.carryover,
    vacationTaken: vacSummary.taken,
    vacationBalance: vacSummary.available,
    vacationAccrued: vacSummary.accrued,
    monthlyBreakdown,
    vacationEntries,
    monthlyTarget,
    selectedYear,
    contractStartDate: contractStart,
    isLoading,
    hasEmployeeRecord,
    employeeId,
    bookVacation,
    deleteVacation,
  };
}
